import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { quarterlyReportApi, GetQuarterlyReportsParams } from '../services/quarterlyReports'
import {
  QuarterlyReport,
  QuarterlyReportCreate,
  QuarterlyReportUpdate,
} from '../types/quarterlyReport'

export const quarterlyReportKeys = {
  all: ['quarterly-reports'] as const,
  lists: () => [...quarterlyReportKeys.all, 'list'] as const,
  list: (params?: GetQuarterlyReportsParams) => [...quarterlyReportKeys.lists(), params] as const,
  details: () => [...quarterlyReportKeys.all, 'detail'] as const,
  detail: (id: number) => [...quarterlyReportKeys.details(), id] as const,
  invoices: (id: number) => [...quarterlyReportKeys.all, 'invoices', id] as const,
}

export function useQuarterlyReportsList(params?: GetQuarterlyReportsParams) {
  return useQuery({
    queryKey: quarterlyReportKeys.list(params),
    queryFn: () => quarterlyReportApi.getAll(params),
  })
}

export function useQuarterlyReport(id: number) {
  return useQuery({
    queryKey: quarterlyReportKeys.detail(id),
    queryFn: () => quarterlyReportApi.getById(id),
    enabled: !!id,
  })
}

export function useQuarterlyReportInvoices(id: number) {
  return useQuery({
    queryKey: quarterlyReportKeys.invoices(id),
    queryFn: () => quarterlyReportApi.getInvoices(id),
    enabled: !!id,
  })
}

export function useCreateQuarterlyReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: QuarterlyReportCreate) => quarterlyReportApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quarterlyReportKeys.lists() })
    },
  })
}

export function useUpdateQuarterlyReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuarterlyReportUpdate }) =>
      quarterlyReportApi.update(id, data),
    onSuccess: (updated: QuarterlyReport) => {
      queryClient.setQueryData(quarterlyReportKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: quarterlyReportKeys.lists() })
    },
  })
}

export function useFinalizeQuarterlyReport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => quarterlyReportApi.finalize(id),
    onSuccess: (updated: QuarterlyReport) => {
      queryClient.setQueryData(quarterlyReportKeys.detail(updated.id), updated)
      queryClient.invalidateQueries({ queryKey: quarterlyReportKeys.lists() })
    },
  })
}

export function useUploadQuarterlyReportDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ reportId, file, description }: { reportId: number; file: File; description?: string }) =>
      quarterlyReportApi.uploadDocument(reportId, file, description),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: quarterlyReportKeys.detail(reportId) })
    },
  })
}

export function useDeleteQuarterlyReportDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ reportId, docId }: { reportId: number; docId: number }) =>
      quarterlyReportApi.deleteDocument(reportId, docId),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: quarterlyReportKeys.detail(reportId) })
    },
  })
}
